import React, { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  List,
  ListItem,
  ListItemText,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import PrintIcon from '@mui/icons-material/Print';
import RefreshIcon from '@mui/icons-material/Refresh';
import InventoryIcon from '@mui/icons-material/Inventory';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import RestaurantMenuIcon from '@mui/icons-material/RestaurantMenu';
import { useNavigate } from 'react-router-dom';
import authService from '../../services/authService';
import { useStockReport, type UseStockReportFilters } from '../../hooks/useStockReport';
import type { StockReportItem } from '../../types/reports';

interface StockFiltersState {
  startDate: string;
  endDate: string;
  searchTerm: string;
  category: string;
  includeInactive: boolean;
  includeZeroBalances: boolean;
  movementPageSize: number;
  maxMovementPages: number;
  cnpj: string;
}

const formatDateInput = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const formatCurrency = (value: number) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value ?? 0);

const formatQuantity = (value: number, unit?: string) =>
  `${value.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}${unit ? ` ${unit}` : ''}`;

const formatDateTime = (value: string) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }
  return new Intl.DateTimeFormat('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
};

const formatPlainNumber = (value: number, fractionDigits = 2) =>
  value.toLocaleString('pt-BR', { minimumFractionDigits: fractionDigits, maximumFractionDigits: fractionDigits });

const formatPercentage = (value?: number) => {
  if (value === undefined || value === null || Number.isNaN(value)) {
    return undefined;
  }
  return `${value.toLocaleString('pt-BR', { minimumFractionDigits: 0, maximumFractionDigits: 1 })}%`;
};

type MovementRowType = 'ENTRIES' | 'CONSUMPTIONS';

interface MovementRow {
  id: string;
  itemId: string;
  itemName: string;
  type: MovementRowType;
  quantity: number;
  totalCost: number;
  unit?: string;
}

const MOVEMENT_LABEL: Record<MovementRowType, string> = {
  ENTRIES: 'Entradas',
  CONSUMPTIONS: 'Consumos',
};

const StockReportPage: React.FC = () => {
  const navigate = useNavigate();
  const [filters, setFilters] = useState<StockFiltersState>(() => {
    const now = new Date();
    const weekAgo = new Date(now);
    weekAgo.setDate(now.getDate() - 7);

    return {
      startDate: formatDateInput(weekAgo),
      endDate: formatDateInput(now),
      searchTerm: '',
      category: '',
      includeInactive: false,
      includeZeroBalances: false,
      movementPageSize: 50,
      maxMovementPages: 3,
      cnpj: '',
    };
  });

  const user = useMemo(() => authService.getUser(), []);

  const queryParams = useMemo<UseStockReportFilters>(() => {
    const payload: UseStockReportFilters = {};

    if (filters.startDate) {
      payload.startDate = new Date(`${filters.startDate}T00:00:00`).toISOString();
    }
    if (filters.endDate) {
      payload.endDate = new Date(`${filters.endDate}T23:59:59`).toISOString();
    }
    if (filters.searchTerm.trim()) {
      payload.searchTerm = filters.searchTerm.trim();
    }
    if (filters.category.trim()) {
      payload.category = filters.category.trim();
    }
    payload.includeInactive = filters.includeInactive;
    payload.includeZeroBalances = filters.includeZeroBalances;
    payload.movementPageSize = filters.movementPageSize;
    payload.maxMovementPages = filters.maxMovementPages;
    const cnpjInput = filters.cnpj.trim();
    const effectiveCnpj = cnpjInput || user?.cnpj || '';
    if (effectiveCnpj) {
      payload.cnpj = effectiveCnpj.replace(/\D/g, '');
    }

    return payload;
  }, [filters, user?.cnpj]);

  const { data, isLoading, isError, isFetching, refetch } = useStockReport(queryParams, true);

  const summary = data?.summary;
  const items = useMemo<StockReportItem[]>(() => data?.items ?? [], [data]);

  const summaryStats = useMemo(
    () => {
      if (summary) {
        return {
          totalItems: summary.totalItems,
          totalQuantity: summary.totalQuantity,
          totalAvailable: summary.totalAvailable,
          totalValue: summary.totalValue,
        };
      }

      return items.reduce(
        (acc, item) => ({
          totalItems: acc.totalItems + 1,
          totalQuantity: acc.totalQuantity + (item.quantity ?? 0),
          totalAvailable: acc.totalAvailable + (item.availableQuantity ?? 0),
          totalValue: acc.totalValue + (item.totalValue ?? 0),
        }),
        { totalItems: 0, totalQuantity: 0, totalAvailable: 0, totalValue: 0 },
      );
    },
    [summary, items],
  );

  const movementRows = useMemo<MovementRow[]>(() => {
    const rows: MovementRow[] = [];

    items.forEach((item) => {
      if (item.entries.quantity || item.entries.totalCost) {
        rows.push({
          id: `${item.itemId}-entries`,
          itemId: item.itemId,
          itemName: item.name,
          type: 'ENTRIES',
          quantity: item.entries.quantity,
          totalCost: item.entries.totalCost,
          unit: item.unit,
        });
      }

      if (item.consumptions.quantity || item.consumptions.totalCost) {
        rows.push({
          id: `${item.itemId}-consumptions`,
          itemId: item.itemId,
          itemName: item.name,
          type: 'CONSUMPTIONS',
          quantity: item.consumptions.quantity,
          totalCost: item.consumptions.totalCost,
          unit: item.unit,
        });
      }
    });

    return rows.sort((a, b) => Math.abs(b.totalCost) - Math.abs(a.totalCost));
  }, [items]);

  const recipeItems = useMemo(() => items.filter((item) => item.recipeComponents.length > 0), [items]);

  const itemsWithLayers = useMemo(
    () => items.filter((item) => item.fifoLayers && item.fifoLayers.length > 0),
    [items],
  );

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    setFilters((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleBooleanToggle = (name: keyof StockFiltersState) => (
    _event: React.MouseEvent<HTMLElement>,
    value: boolean,
  ) => {
    if (typeof value !== 'boolean') {
      return;
    }
    setFilters((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleNumberChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    const parsed = Number(value);
    setFilters((prev) => ({
      ...prev,
      [name]: Number.isNaN(parsed) ? prev[name as keyof StockFiltersState] : parsed,
    }));
  };

  const handleRefresh = () => {
    refetch();
  };

  const handleNavigateToPrint = () => {
    const params = new URLSearchParams();
    if (filters.startDate) params.set('startDate', filters.startDate);
    if (filters.endDate) params.set('endDate', filters.endDate);
    if (filters.searchTerm.trim()) params.set('searchTerm', filters.searchTerm.trim());
    if (filters.category.trim()) params.set('category', filters.category.trim());
    if (filters.includeInactive) params.set('includeInactive', 'true');
    if (filters.includeZeroBalances) params.set('includeZeroBalances', 'true');
    params.set('movementPageSize', String(filters.movementPageSize));
    params.set('maxMovementPages', String(filters.maxMovementPages));
    const cnpjInput = filters.cnpj.trim();
    const effectiveCnpj = cnpjInput || user?.cnpj || '';
    if (effectiveCnpj) params.set('cnpj', effectiveCnpj.replace(/\D/g, ''));

    navigate(`/reports/stock/print?${params.toString()}`);
  };

  const renderSummaryCards = () => (
    <Box
      className="screen-only"
      display="grid"
      gap={2}
      gridTemplateColumns={{ xs: '1fr', md: 'repeat(3, minmax(0, 1fr))' }}
    >
      <Card>
        <CardContent>
          <Box display="flex" alignItems="center" gap={1}>
            <InventoryIcon color="primary" />
            <Box>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Itens monitorados
              </Typography>
              <Typography variant="h4">{summaryStats.totalItems}</Typography>
            </Box>
          </Box>
        </CardContent>
      </Card>
      <Card>
        <CardContent>
          <Box display="flex" alignItems="center" gap={1}>
            <CompareArrowsIcon color="primary" />
            <Box>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Quantidade disponível
              </Typography>
              <Typography variant="h4">{formatQuantity(summaryStats.totalAvailable)}</Typography>
            </Box>
          </Box>
        </CardContent>
      </Card>
      <Card>
        <CardContent>
          <Box display="flex" alignItems="center" gap={1}>
            <RestaurantMenuIcon color="primary" />
            <Box>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Valor total estimado
              </Typography>
              <Typography variant="h4">{formatCurrency(summaryStats.totalValue)}</Typography>
            </Box>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );

  const renderStockIndicators = () => {
    if (!summary) {
      return null;
    }

    return (
      <Card className="screen-only">
        <CardContent>
          <Typography variant="subtitle1" gutterBottom>
            Indicadores de estoque
          </Typography>
          <Box display="flex" flexWrap="wrap" gap={1}>
            <Chip
              color={summary.itemsBelowMinimum > 0 ? 'warning' : 'default'}
              label={`Itens abaixo do mínimo: ${summary.itemsBelowMinimum}`}
            />
            <Chip
              color={summary.itemsAboveMaximum > 0 ? 'info' : 'default'}
              label={`Itens acima do máximo: ${summary.itemsAboveMaximum}`}
            />
            <Chip
              variant="outlined"
              label={`Quantidade total: ${formatPlainNumber(summary.totalQuantity)} un`}
            />
            <Chip
              variant="outlined"
              label={`Entradas no período: ${formatPlainNumber(summary.entriesQuantity)} un`}
            />
            <Chip variant="outlined" label={`Custo de entradas: ${formatCurrency(summary.entriesValue)}`} />
            <Chip
              variant="outlined"
              label={`Consumos no período: ${formatPlainNumber(summary.consumptionsQuantity)} un`}
            />
            <Chip
              variant="outlined"
              label={`Custo de consumos: ${formatCurrency(summary.consumptionsValue)}`}
            />
          </Box>
        </CardContent>
      </Card>
    );
  };

  const renderBalanceTable = () => (
    <Card>
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h6">Saldos consolidados</Typography>
          {isFetching && <CircularProgress size={24} />}
        </Box>
        {items.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            Nenhum saldo encontrado. Ajuste filtros ou verifique se há movimentações no período.
          </Typography>
        ) : (
          <Box overflow="auto">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Item</TableCell>
                  <TableCell>Categoria</TableCell>
                  <TableCell align="right">Quantidade total</TableCell>
                  <TableCell align="right">Reservado</TableCell>
                  <TableCell align="right">Disponível</TableCell>
                  <TableCell align="right">Custo médio</TableCell>
                  <TableCell align="right">Valor total</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {items.map((item) => {
                  const totalValue =
                    item.totalValue ?? (typeof item.averageCost === 'number' ? item.averageCost * item.quantity : 0);

                  return (
                    <TableRow key={item.itemId} hover>
                      <TableCell>
                        <Box display="flex" flexDirection="column">
                          <Typography variant="subtitle2">{item.name}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            SKU: {item.sku ?? '—'}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Última movimentação: {item.lastMovementAt ? formatDateTime(item.lastMovementAt) : '—'}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>{item.category ?? '—'}</TableCell>
                      <TableCell align="right">{formatQuantity(item.quantity, item.unit)}</TableCell>
                      <TableCell align="right">{formatQuantity(item.reservedQuantity, item.unit)}</TableCell>
                      <TableCell align="right">{formatQuantity(item.availableQuantity, item.unit)}</TableCell>
                      <TableCell align="right">{formatCurrency(item.averageCost ?? 0)}</TableCell>
                      <TableCell align="right">{formatCurrency(totalValue ?? 0)}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Box>
        )}
      </CardContent>
    </Card>
  );

  const renderMovementsTable = () => (
    <Card>
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h6">Entradas e consumos agregados</Typography>
          {isFetching && <CircularProgress size={24} />}
        </Box>
        {movementRows.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            Nenhuma movimentação contabilizada para o período filtrado.
          </Typography>
        ) : (
          <Box overflow="auto">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Item</TableCell>
                  <TableCell>Tipo</TableCell>
                  <TableCell align="right">Quantidade</TableCell>
                  <TableCell align="right">Valor total</TableCell>
                  <TableCell align="right">Custo médio</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {movementRows.map((row) => {
                  const averageCost = row.quantity ? row.totalCost / row.quantity : 0;

                  return (
                    <TableRow key={row.id}>
                      <TableCell>{row.itemName}</TableCell>
                      <TableCell>{MOVEMENT_LABEL[row.type]}</TableCell>
                      <TableCell align="right">{formatQuantity(row.quantity, row.unit)}</TableCell>
                      <TableCell align="right">{formatCurrency(row.totalCost)}</TableCell>
                      <TableCell align="right">{formatCurrency(averageCost)}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Box>
        )}
      </CardContent>
    </Card>
  );

  const renderRecipeList = () => (
    <Card>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Fichas técnicas utilizadas
        </Typography>
        {recipeItems.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            Nenhuma ficha técnica registrada para os itens filtrados.
          </Typography>
        ) : (
          <List dense>
            {recipeItems.map((item) => (
              <ListItem key={item.itemId} alignItems="flex-start" divider>
                <ListItemText
                  primary={
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                      <Typography variant="subtitle2">{item.name}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        Estoque disponível: {formatQuantity(item.availableQuantity, item.unit)}
                      </Typography>
                    </Box>
                  }
                  secondary={
                    <Box display="flex" flexDirection="column" gap={1} mt={1}>
                      {item.recipeComponents.map((component) => {
                        const yieldText = formatPercentage(component.yieldPercentage);

                        return (
                          <Box
                            key={`${item.itemId}-${component.componentItemId}`}
                            display="flex"
                            alignItems="center"
                            gap={1}
                            flexWrap="wrap"
                          >
                            <Chip
                              size="small"
                              variant="outlined"
                              label={component.componentName ?? `Item ${component.componentItemId}`}
                            />
                            <Typography variant="caption" color="text.secondary">
                              {formatQuantity(component.quantity, component.unit)}
                              {yieldText ? ` • rendimento ${yieldText}` : ''}
                            </Typography>
                          </Box>
                        );
                      })}
                    </Box>
                  }
                />
              </ListItem>
            ))}
          </List>
        )}
      </CardContent>
    </Card>
  );

  const renderCostLayers = () => {
    if (itemsWithLayers.length === 0) {
      return null;
    }

    return (
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Camadas de custo (FIFO)
          </Typography>
          <Box
            display="grid"
            gap={2}
            gridTemplateColumns={{ xs: '1fr', md: 'repeat(2, minmax(0, 1fr))', lg: 'repeat(3, minmax(0, 1fr))' }}
          >
            {itemsWithLayers.map((item) => (
              <Card variant="outlined" key={item.itemId}>
                <CardContent>
                  <Typography variant="subtitle2" gutterBottom>
                    {item.name}
                  </Typography>
                  <Box display="flex" flexDirection="column" gap={1}>
                    {item.fifoLayers.map((layer) => (
                      <Box key={`${item.itemId}-${layer.lotId ?? 'layer'}`}>
                        <Typography variant="body2" color="text.secondary">
                          Lote: {layer.lotId ?? '—'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Quantidade: {formatQuantity(layer.quantity, item.unit)} • Custo acumulado:{' '}
                          {formatCurrency(layer.unitCost * layer.quantity)}
                        </Typography>
                        {layer.receivedAt && (
                          <Typography variant="caption" color="text.secondary">
                            Recebido em: {formatDateTime(layer.receivedAt)}
                          </Typography>
                        )}
                      </Box>
                    ))}
                  </Box>
                </CardContent>
              </Card>
            ))}
          </Box>
        </CardContent>
      </Card>
    );
  };

  return (
    <Box display="flex" flexDirection="column" gap={3}>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems={{ xs: 'flex-start', md: 'center' }}
        flexWrap="wrap"
        gap={2}
        className="screen-only"
      >
        <Box>
          <Typography variant="h4" gutterBottom>
            Relatório de estoque
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Acompanhe os saldos disponíveis, movimentações recentes e o uso de fichas técnicas para garantir um
            controle de estoque preciso.
          </Typography>
        </Box>
        <Box display="flex" gap={1}>
          <Button
            variant="outlined"
            startIcon={<PrintIcon />}
            onClick={handleNavigateToPrint}
            disabled={isLoading || isFetching}
          >
            Imprimir
          </Button>
          <Button
            variant="contained"
            startIcon={<RefreshIcon />}
            onClick={handleRefresh}
            disabled={isLoading}
          >
            Atualizar
          </Button>
        </Box>
      </Box>

      <Card className="screen-only">
        <CardContent>
          <Box
            display="grid"
            gap={2}
            gridTemplateColumns={{ xs: '1fr', md: 'repeat(4, minmax(0, 1fr))' }}
          >
            <Box>
              <TextField
                label="Data inicial"
                name="startDate"
                type="date"
                value={filters.startDate}
                onChange={handleInputChange}
                fullWidth
                size="small"
                InputLabelProps={{ shrink: true }}
              />
            </Box>
            <Box>
              <TextField
                label="Data final"
                name="endDate"
                type="date"
                value={filters.endDate}
                onChange={handleInputChange}
                fullWidth
                size="small"
                InputLabelProps={{ shrink: true }}
              />
            </Box>
            <Box>
              <TextField
                label="Buscar item"
                name="searchTerm"
                value={filters.searchTerm}
                onChange={handleInputChange}
                fullWidth
                size="small"
                placeholder="Nome, SKU ou palavra-chave"
              />
            </Box>
            <Box>
              <TextField
                label="Categoria"
                name="category"
                value={filters.category}
                onChange={handleInputChange}
                fullWidth
                size="small"
                placeholder="Ex: Proteínas"
              />
            </Box>
            <Box>
              <TextField
                label="CNPJ"
                name="cnpj"
                value={filters.cnpj}
                onChange={handleInputChange}
                fullWidth
                size="small"
                placeholder="Somente números"
                helperText="Visível apenas para usuários desenvolvedores"
              />
            </Box>
            <Box>
              <TextField
                label="Itens por página (movimentações)"
                name="movementPageSize"
                select
                SelectProps={{ native: true }}
                value={filters.movementPageSize}
                onChange={handleNumberChange}
                fullWidth
                size="small"
              >
                {[25, 50, 100].map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </TextField>
            </Box>
            <Box>
              <TextField
                label="Páginas máximas de movimentações"
                name="maxMovementPages"
                select
                SelectProps={{ native: true }}
                value={filters.maxMovementPages}
                onChange={handleNumberChange}
                fullWidth
                size="small"
              >
                {[1, 2, 3, 5].map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </TextField>
            </Box>
            <Box display="flex" flexDirection="column" gap={1}>
              <Typography variant="subtitle1" gutterBottom>
                Incluir inativos
              </Typography>
              <ToggleButtonGroup
                exclusive
                value={filters.includeInactive}
                onChange={handleBooleanToggle('includeInactive')}
                size="small"
              >
                <ToggleButton value={false}>Não</ToggleButton>
                <ToggleButton value={true}>Sim</ToggleButton>
              </ToggleButtonGroup>
            </Box>
            <Box display="flex" flexDirection="column" gap={1}>
              <Typography variant="subtitle1" gutterBottom>
                Incluir itens zerados
              </Typography>
              <ToggleButtonGroup
                exclusive
                value={filters.includeZeroBalances}
                onChange={handleBooleanToggle('includeZeroBalances')}
                size="small"
              >
                <ToggleButton value={false}>Não</ToggleButton>
                <ToggleButton value={true}>Sim</ToggleButton>
              </ToggleButtonGroup>
            </Box>
          </Box>
        </CardContent>
        <Divider />
      </Card>

      {isError && (
        <Alert severity="error">
          Não foi possível carregar o relatório de estoque. Ajuste os filtros e tente novamente.
        </Alert>
      )}

      {isLoading ? (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight={320}>
          <CircularProgress size={48} />
        </Box>
      ) : (
        <>
          {renderSummaryCards()}
          {renderStockIndicators()}
          {renderBalanceTable()}
          {renderMovementsTable()}
          {renderRecipeList()}
          {renderCostLayers()}
        </>
      )}
    </Box>
  );
};

export default StockReportPage;
